import { Component, EventEmitter, Input, Output, signal, OnDestroy, computed } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { merge, Subject, takeUntil, tap } from 'rxjs';
import moment from 'moment';
import { Filter } from '../../filters';
import { CompoundFilterConfig } from '../../../compound-filter-selection/compound-filter-selection.model';
import { FilterOptionComponent } from '../filter-option/filter-option.component';
@Component({
  selector: 'cbre-range',
  imports: [
    ReactiveFormsModule,
    MatMomentDateModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    FilterOptionComponent,
  ],
  templateUrl: './range.component.html',
  styleUrls: ['./range.component.scss'],
})
export class RangeFilterComponent implements OnDestroy {
  @Input() set form(value: FormGroup) {
    this._form = value;
    this._setUpFilter();
  }

  get form(): FormGroup {
    return this._form;
  }

  public readonly filter$ = signal<Filter>(null);
  @Input() set filter(filter: Filter) {
    this.filter$.set(filter);
    this._setUpFilter();
  }
  compoundFilterConfig = signal<CompoundFilterConfig | null>(null);
  showOptionMenuIcon = computed(() => !this.filter$()?.hideOptionMenuIcon);

  @Output() filterOptionChange = new EventEmitter<Filter>();

  @Input() showLabel = true;

  public fromCtrl: FormControl;
  public toCtrl: FormControl;
  public readonly hasFromCtrlValue = signal(false);
  public readonly hasToCtrlValue = signal(false);

  private _form: FormGroup;
  private _destroy$ = new Subject();

  private _setUpFilter() {
    if (this.filter$() == null || this._form == null) return;
    const key = String(this.filter$().key);
    if (!key) return;

    this.fromCtrl = this._form.get(key).get('from') as FormControl;
    this.toCtrl = this._form.get(key).get('to') as FormControl;
    this._checkValuePresence();

    this._destroy$.next(true);
    merge(this._sanitizeCtrlStram(this.fromCtrl), this._sanitizeCtrlStram(this.toCtrl))
      .pipe(takeUntil(this._destroy$))
      .subscribe(this._checkValuePresence);
  }

  private _checkValuePresence = () => {
    this.hasFromCtrlValue.set(!!this.fromCtrl?.value);
    this.hasToCtrlValue.set(!!this.toCtrl?.value);
  };

  private _sanitizeCtrlStram(ctrl: FormControl) {
    if (this.filter$()?.dataType === 'date') {
      return ctrl.valueChanges.pipe(
        tap(value => ctrl.setValue(this._sanitizeDateValue(value), { emitEvent: false, onlySelf: true }))
      );
    }
    return ctrl.valueChanges;
  }

  private _sanitizeDateValue(value: string) {
    const utcInputFormat = `${moment.HTML5_FMT.DATETIME_LOCAL}Z`; // this is the resulting value => 'YYYY-MM-DDTHH:mmZ'
    const utcOutputFormat = 'YYYY-MM-DDTHH:mm:ss[Z]';
    const dateValue = moment(value, utcInputFormat).utc();
    return dateValue.isValid() ? dateValue.format(utcOutputFormat) : null;
  }

  dateEvent(type: 'from' | 'to', event: { value: any }) {
    const ctrl = type === 'from' ? this.fromCtrl : this.toCtrl;
    ctrl.setValue(this._sanitizeDateValue(event.value));
  }

  clearRangeFilter(filterType: 'from' | 'to', event: MouseEvent) {
    // 1. Stop event from rising to the mat-form-field container
    event.stopPropagation();

    // 2. Prevent the native input text field from gaining focus/opening popup
    event.preventDefault();

    if (filterType === 'from') {
      this.fromCtrl.setValue(null);
      return;
    }
    this.toCtrl.setValue(null);
  }

  ngOnDestroy() {
    this._destroy$.next(true);
  }
}
